// ===================================================================
// PA GRANTS — Request for Reimbursement (RFR) Management
// Dual-mode: Grantee review queue + Applicant submission view
// List/Detail pattern with inline review actions
// ===================================================================

import { useState, useEffect, useMemo, useCallback } from 'react';
import { usePolling } from '../../hooks/usePolling';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select, MultiSelect,
  Textarea, Modal, Tabs, SimpleGrid, ActionIcon, Table,
  NumberInput, Divider, ThemeIcon, Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconPlus, IconEdit, IconTrash, IconSearch, IconDownload, IconReceipt,
  IconCheck, IconX, IconArrowLeft, IconArrowBack, IconCash, IconLink,
  IconRowInsertBottom, IconSend,
} from '@tabler/icons-react';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useAuthStore } from '../../store/authStore';
import { useRoleStore } from '../../store/roleStore';
import { useMyPAReimbursements, useMyPAProjects } from '../../hooks/useMyPAOrgData';
import { exportToCSV } from '../../lib/exportUtils';
import type { PAReimbursementRequest, PAProject } from '../../types/pagrants';

const APPLICANT_SLUGS = ['pa_applicant_mgr', 'pa_applicant_staff', 'pa_applicant_viewer'];

const STATUS_COLORS: Record<string, string> = {
  draft: 'gray', submitted: 'blue', under_review: 'indigo', approved: 'green',
  denied: 'red', revision_needed: 'orange', paid: 'teal',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review',
  approved: 'Approved', denied: 'Denied', revision_needed: 'Revision Needed', paid: 'Paid',
};

interface LinkedCostItem {
  type: 'cost' | 'labor' | 'equipment' | 'material';
  rid: number;
  description: string;
  amount: number;
}

interface FreeformItem {
  description: string;
  category: string;
  amount: number;
}

interface RFRForm {
  projectId: string;
  organizationId: string;
  disasterId: string;
  requestNumber: string;
  requestDate: string;
  periodStart: string;
  periodEnd: string;
  linkedItems: LinkedCostItem[];
  freeformItems: FreeformItem[];
  supportingDocIds: string;
  notes: string;
}

const emptyForm = (): RFRForm => ({
  projectId: '', organizationId: '', disasterId: '',
  requestNumber: '', requestDate: new Date().toISOString().split('T')[0],
  periodStart: '', periodEnd: '',
  linkedItems: [], freeformItems: [{ description: '', category: '', amount: 0 }],
  supportingDocIds: '', notes: '',
});

function nextRFRNumber(existing: PAReimbursementRequest[], project: PAProject | undefined): string {
  const prefix = `RFR-${project?.projectNumber || '000'}`;
  const nums = existing
    .filter(r => r.requestNumber.startsWith(prefix))
    .map(r => parseInt(r.requestNumber.split('-').pop() || '0'))
    .filter(n => !isNaN(n));
  const next = nums.length > 0 ? Math.max(...nums) + 1 : 1;
  return `${prefix}-${String(next).padStart(3, '0')}`;
}

export default function Reimbursements() {
  const {
    reimbursementRequests, projects, organizations,
    projectCosts, laborRecords, equipRecords, materials, documents,
    fetchReimbursements, fetchProjects, fetchOrganizations,
    fetchDisasters, fetchProjectCosts, fetchLabor, fetchEquipment, fetchMaterials, fetchDocuments,
    createReimbursement, updateReimbursement, deleteReimbursement, updateProject,
  } = usePAGrantsStore();
  const user = useAuthStore(s => s.user);
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const isApplicant = APPLICANT_SLUGS.includes(effectiveRole?.roleSlug ?? '');
  const isViewer = effectiveRole?.roleSlug === 'pa_applicant_viewer';

  const myReimbursements = useMyPAReimbursements();
  const myProjects = useMyPAProjects();

  // The source list depends on role: applicants see only their org's, grantees/admins see all
  const sourceList = isApplicant ? myReimbursements : reimbursementRequests;
  const availableProjects = isApplicant ? myProjects : projects;

  const [selected, setSelected] = useState<PAReimbursementRequest | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [orgFilter, setOrgFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PAReimbursementRequest | null>(null);
  const [form, setForm] = useState<RFRForm>(emptyForm());
  const [activeTab, setActiveTab] = useState<string | null>('info');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PAReimbursementRequest | null>(null);

  // Inline review state (detail view)
  const [reviewNotes, setReviewNotes] = useState('');
  const [adjustedAmount, setAdjustedAmount] = useState<number | string>('');
  const [paymentRef, setPaymentRef] = useState('');

  const refreshAll = useCallback(() => {
    fetchReimbursements(); fetchProjects(); fetchOrganizations(); fetchDisasters();
    fetchProjectCosts(); fetchLabor(); fetchEquipment(); fetchMaterials(); fetchDocuments();
  }, [fetchReimbursements, fetchProjects, fetchOrganizations, fetchDisasters, fetchProjectCosts, fetchLabor, fetchEquipment, fetchMaterials, fetchDocuments]);
  useEffect(() => { refreshAll(); }, [refreshAll]);
  usePolling(refreshAll);

  const orgMap = useMemo(() => new Map(organizations.map(o => [String(o.Rid), o])), [organizations]);
  const projMap = useMemo(() => new Map(projects.map(p => [String(p.Rid), p])), [projects]);

  const filtered = useMemo(() => {
    let list = sourceList;
    if (statusFilter) list = list.filter(r => r.status === statusFilter);
    if (orgFilter) list = list.filter(r => r.organizationId === orgFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r => {
        const proj = projMap.get(r.projectId);
        const org = orgMap.get(r.organizationId);
        return (r.requestNumber || '').toLowerCase().includes(q)
          || (proj?.projectNumber || '').toLowerCase().includes(q)
          || (proj?.projectTitle || '').toLowerCase().includes(q)
          || (org?.orgName || '').toLowerCase().includes(q);
      });
    }
    return list;
  }, [sourceList, statusFilter, orgFilter, search, projMap, orgMap]);

  // Summary stats (grantee only)
  const stats = useMemo(() => {
    const pending = sourceList.filter(r => ['submitted', 'under_review'].includes(r.status));
    const approvedThisMonth = sourceList.filter(r => {
      if (r.status !== 'approved' && r.status !== 'paid') return false;
      const d = new Date(r.reviewDate);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });
    const paid = sourceList.filter(r => r.status === 'paid');
    return {
      pendingCount: pending.length,
      pendingAmount: pending.reduce((s, r) => s + (parseFloat(r.requestedAmount) || 0), 0),
      approvedAmount: approvedThisMonth.reduce((s, r) => s + (parseFloat(r.approvedAmount) || 0), 0),
      paidAmount: paid.reduce((s, r) => s + (parseFloat(r.approvedAmount) || 0), 0),
    };
  }, [sourceList]);

  const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  // Get available cost items for a project
  function getProjectCostItems(projectId: string): LinkedCostItem[] {
    const items: LinkedCostItem[] = [];
    projectCosts.filter(c => c.projectId === projectId).forEach(c =>
      items.push({ type: 'cost', rid: c.Rid, description: `${c.costType}: ${c.description}`, amount: parseFloat(c.totalCost) || 0 })
    );
    laborRecords.filter(l => l.projectId === projectId).forEach(l =>
      items.push({ type: 'labor', rid: l.Rid, description: `Labor: ${l.employeeName} (${l.workDate})`, amount: parseFloat(l.totalCost) || 0 })
    );
    equipRecords.filter(e => e.projectId === projectId).forEach(e =>
      items.push({ type: 'equipment', rid: e.Rid, description: `Equip: ${e.equipmentType} (${e.workDate})`, amount: parseFloat(e.totalCost) || 0 })
    );
    materials.filter(m => m.projectId === projectId).forEach(m =>
      items.push({ type: 'material', rid: m.Rid, description: `Material: ${m.materialDescription}`, amount: parseFloat(m.totalCost) || 0 })
    );
    return items;
  }

  function computeTotals(f: RFRForm, proj?: PAProject) {
    const linkedTotal = f.linkedItems.reduce((s, i) => s + i.amount, 0);
    const freeformTotal = f.freeformItems.reduce((s, i) => s + (i.amount || 0), 0);
    const total = linkedTotal + freeformTotal;
    const pct = parseFloat(proj?.federalSharePct || '75') / 100;
    return { total, federalShare: total * pct, applicantMatch: total * (1 - pct) };
  }

  // After any mutation, refresh data and update selected if still viewing
  async function refreshAndReselect(rid: number) {
    await fetchReimbursements();
    // Re-select from the fresh store snapshot
    const fresh = usePAGrantsStore.getState().reimbursementRequests.find(r => r.Rid === rid);
    setSelected(fresh || null);
  }

  // ── Modal helpers ──────────────────────────────────────────────

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setActiveTab('info');
    setModalOpen(true);
  }

  function openEdit(rfr: PAReimbursementRequest) {
    setEditing(rfr);
    let linkedItems: LinkedCostItem[] = [];
    let freeformItems: FreeformItem[] = [{ description: '', category: '', amount: 0 }];
    try {
      const bd = JSON.parse(rfr.costBreakdown || '{}');
      if (bd.linked) linkedItems = bd.linked;
      if (bd.freeform?.length) freeformItems = bd.freeform;
    } catch { /* ignore parse error */ }
    setForm({
      projectId: rfr.projectId, organizationId: rfr.organizationId, disasterId: rfr.disasterId,
      requestNumber: rfr.requestNumber, requestDate: rfr.requestDate,
      periodStart: rfr.periodStart, periodEnd: rfr.periodEnd,
      linkedItems, freeformItems,
      supportingDocIds: rfr.supportingDocIds, notes: rfr.notes,
    });
    setActiveTab('info');
    setModalOpen(true);
  }

  function handleProjectChange(projectId: string) {
    const proj = projMap.get(projectId);
    if (!proj) return;
    setForm(f => ({
      ...f,
      projectId,
      organizationId: proj.organizationId,
      disasterId: proj.disasterId,
      requestNumber: nextRFRNumber(reimbursementRequests, proj),
      linkedItems: [],
    }));
  }

  function toggleLinkedItem(item: LinkedCostItem) {
    setForm(f => {
      const exists = f.linkedItems.find(i => i.type === item.type && i.rid === item.rid);
      return {
        ...f,
        linkedItems: exists
          ? f.linkedItems.filter(i => !(i.type === item.type && i.rid === item.rid))
          : [...f.linkedItems, item],
      };
    });
  }

  async function handleSave(submitAfterSave = false) {
    if (!form.projectId) return;
    setSaving(true);
    try {
      const proj = projMap.get(form.projectId);
      const totals = computeTotals(form, proj);
      const data: Partial<PAReimbursementRequest> = {
        projectId: form.projectId,
        organizationId: form.organizationId,
        disasterId: form.disasterId,
        requestNumber: form.requestNumber,
        requestDate: form.requestDate,
        periodStart: form.periodStart,
        periodEnd: form.periodEnd,
        requestedAmount: String(totals.total),
        federalShare: String(totals.federalShare),
        applicantMatch: String(totals.applicantMatch),
        costBreakdown: JSON.stringify({ linked: form.linkedItems, freeform: form.freeformItems.filter(i => i.description || i.amount) }),
        supportingDocIds: form.supportingDocIds,
        status: submitAfterSave ? 'submitted' : (editing?.status || 'draft'),
        submittedBy: submitAfterSave ? (user?.fullName || user?.email || '') : (editing?.submittedBy || ''),
        submittedDate: submitAfterSave ? new Date().toISOString().split('T')[0] : (editing?.submittedDate || ''),
        notes: form.notes,
      };

      if (editing) {
        await updateReimbursement(editing.Rid, data);
        await refreshAndReselect(editing.Rid);
      } else {
        await createReimbursement(data);
        await fetchReimbursements();
      }
      setModalOpen(false);
    } finally { setSaving(false); }
  }

  // ── Detail view actions ────────────────────────────────────────

  async function handleRetract(rfr: PAReimbursementRequest) {
    setSaving(true);
    try {
      await updateReimbursement(rfr.Rid, { status: 'draft', submittedBy: '', submittedDate: '' });
      await refreshAndReselect(rfr.Rid);
    } finally { setSaving(false); }
  }

  async function handleReviewAction(rfr: PAReimbursementRequest, action: 'approve' | 'deny' | 'revision') {
    // Deny and revision require notes
    if ((action === 'deny' || action === 'revision') && !reviewNotes.trim()) {
      notifications.show({
        title: action === 'deny' ? 'Denial notes required' : 'Revision notes required',
        message: `Please provide notes explaining the reason for ${action === 'deny' ? 'denial' : 'revision request'}.`,
        color: 'orange',
      });
      return;
    }

    // Validate approval amount against remaining obligation
    if (action === 'approve') {
      const amt = typeof adjustedAmount === 'number' ? adjustedAmount : parseFloat(rfr.requestedAmount) || 0;
      const proj = projMap.get(rfr.projectId);
      if (proj) {
        const obligated = parseFloat(proj.obligatedAmount) || 0;
        const drawn = parseFloat(proj.drawdownAmount) || 0;
        const remaining = obligated - drawn;
        if (amt > remaining) {
          notifications.show({
            title: 'Amount exceeds remaining obligation',
            message: `Approved amount (${fmt(amt)}) exceeds remaining obligation balance (${fmt(remaining)}). Obligated: ${fmt(obligated)}, Already Drawn: ${fmt(drawn)}.`,
            color: 'red',
            autoClose: 8000,
          });
          return;
        }
      }
    }

    setSaving(true);
    try {
      const updates: Partial<PAReimbursementRequest> = {
        reviewedBy: user?.fullName || user?.email || '',
        reviewDate: new Date().toISOString().split('T')[0],
        reviewNotes,
      };

      if (action === 'approve') {
        const amt = typeof adjustedAmount === 'number' ? adjustedAmount : parseFloat(rfr.requestedAmount) || 0;
        updates.status = 'approved';
        updates.approvedAmount = String(amt);
        // Update project drawdown
        try {
          const proj = projMap.get(rfr.projectId);
          if (proj) {
            const currentDrawdown = parseFloat(proj.drawdownAmount) || 0;
            await updateProject(proj.Rid, { drawdownAmount: String(currentDrawdown + amt) });
          }
        } catch (err) { console.warn('[RFR] Failed to update project drawdown:', err); }
      } else if (action === 'deny') {
        updates.status = 'denied';
      } else {
        updates.status = 'revision_needed';
      }

      await updateReimbursement(rfr.Rid, updates);

      // Fire workflow trigger on approval
      if (action === 'approve') {
        try {
          const { useWorkflowStore } = await import('../../store/workflowStore');
          const proj = projMap.get(rfr.projectId);
          const org = orgMap.get(rfr.organizationId);
          const amt = typeof adjustedAmount === 'number' ? adjustedAmount : parseFloat(rfr.requestedAmount) || 0;

          // Build readable cost summary — NEVER pass raw JSON (Ignatius strips escapes, breaking nested JSON)
          const costLines: string[] = [];
          try {
            const bd = JSON.parse(rfr.costBreakdown || '{}');
            if (bd.linked?.length) for (const item of bd.linked) costLines.push(`${item.description}: ${fmt(item.amount)}`);
            if (bd.freeform?.length) for (const item of bd.freeform) { if (item.description || item.amount) costLines.push(`${item.description} (${item.category}): ${fmt(item.amount)}`); }
          } catch { /* parse failed — use fallback */ }
          const costSummary = costLines.length > 0
            ? costLines.join('; ')
            : `Total: ${fmt(parseFloat(rfr.requestedAmount) || 0)}`;

          await useWorkflowStore.getState().checkAndTriggerWorkflows(
            'rfr_approved', 'pagrants', 'yuqknqww3',
            String(rfr.Rid), rfr.requestNumber || 'RFR',
            user?.email || 'system', undefined,
            {
              requestNumber: rfr.requestNumber,
              projectNumber: proj?.projectNumber || '',
              projectTitle: proj?.projectTitle || '',
              applicant: org?.orgName || '',
              requestedAmount: fmt(parseFloat(rfr.requestedAmount) || 0),
              approvedAmount: fmt(amt),
              federalShare: fmt(parseFloat(rfr.federalShare) || 0),
              applicantMatch: fmt(parseFloat(rfr.applicantMatch) || 0),
              periodStart: rfr.periodStart,
              periodEnd: rfr.periodEnd,
              costBreakdown: costSummary,
              reviewedBy: updates.reviewedBy || '',
              reviewDate: updates.reviewDate || '',
              reviewNotes: reviewNotes,
              status: 'approved',
              link: `${window.location.origin}/pagrants/reimbursements`,
            } as Record<string, unknown>,
          );
        } catch (err) { console.warn('[PA Grants] Workflow trigger failed:', err); }
      }

      setReviewNotes('');
      setAdjustedAmount('');
      await refreshAndReselect(rfr.Rid);
    } catch (err) {
      console.error('[RFR] Review action failed:', err);
      alert(`Failed to ${action} reimbursement: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally { setSaving(false); }
  }

  async function handleMarkPaid(rfr: PAReimbursementRequest) {
    setSaving(true);
    try {
      await updateReimbursement(rfr.Rid, {
        status: 'paid',
        paymentDate: new Date().toISOString().split('T')[0],
        paymentReference: paymentRef,
      });
      setPaymentRef('');
      await refreshAndReselect(rfr.Rid);
    } finally { setSaving(false); }
  }

  async function handleSubmitFromDetail(rfr: PAReimbursementRequest) {
    setSaving(true);
    try {
      await updateReimbursement(rfr.Rid, {
        status: 'submitted',
        submittedBy: user?.fullName || user?.email || '',
        submittedDate: new Date().toISOString().split('T')[0],
      });
      await refreshAndReselect(rfr.Rid);
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    await deleteReimbursement(deleteTarget.Rid);
    setDeleteTarget(null);
    setSelected(null);
  }

  const availableCosts = useMemo(() => form.projectId ? getProjectCostItems(form.projectId) : [], [form.projectId, projectCosts, laborRecords, equipRecords, materials]);
  const formProj = projMap.get(form.projectId);
  const formTotals = computeTotals(form, formProj);

  // ══════════════════════════════════════════════════════════════
  //  DETAIL VIEW
  // ══════════════════════════════════════════════════════════════
  if (selected) {
    const detailProj = projMap.get(selected.projectId);
    const detailOrg = orgMap.get(selected.organizationId);

    // Parse cost breakdown
    let breakdown: { linked?: LinkedCostItem[]; freeform?: FreeformItem[] } = {};
    try { breakdown = JSON.parse(selected.costBreakdown || '{}'); } catch { /* ignore */ }

    const reqAmt = parseFloat(selected.requestedAmount) || 0;
    const fedShare = parseFloat(selected.federalShare) || 0;
    const appMatch = parseFloat(selected.applicantMatch) || 0;

    // Parse supporting doc IDs
    const docIds = (selected.supportingDocIds || '').split(',').map(s => s.trim()).filter(Boolean);
    const linkedDocs = documents.filter(d => docIds.includes(String(d.Rid)));

    return (
      <Stack gap="md">
        {/* Back button + status */}
        <Group justify="space-between">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => { setSelected(null); setReviewNotes(''); setAdjustedAmount(''); setPaymentRef(''); }}
          >
            Back to Reimbursements
          </Button>
          <Badge size="lg" color={STATUS_COLORS[selected.status] || 'gray'}>
            {STATUS_LABELS[selected.status] || selected.status}
          </Badge>
        </Group>

        {/* Title */}
        <div>
          <Title order={3}>
            <IconReceipt size={22} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            {selected.requestNumber || 'Untitled RFR'}
            {detailProj ? ` \u2014 ${detailProj.projectTitle}` : ''}
          </Title>
          <Text size="sm" c="dimmed" mt={4}>
            {detailOrg ? `Applicant: ${detailOrg.orgName}` : ''}
            {detailProj ? ` | Project: ${detailProj.projectNumber}` : ''}
          </Text>
        </div>

        {/* Request Info */}
        <Paper withBorder p="md">
          <Text fw={600} mb="xs">Request Info</Text>
          <SimpleGrid cols={2} spacing="sm">
            <div>
              <Text size="xs" c="dimmed">Period</Text>
              <Text size="sm">{selected.periodStart || '—'} to {selected.periodEnd || '—'}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Request Date</Text>
              <Text size="sm">{selected.requestDate || '—'}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Submitted</Text>
              <Text size="sm">
                {selected.submittedDate ? `${selected.submittedDate} by ${selected.submittedBy}` : 'Not yet submitted'}
              </Text>
            </div>
            {selected.notes && (
              <div>
                <Text size="xs" c="dimmed">Notes</Text>
                <Text size="sm">{selected.notes}</Text>
              </div>
            )}
          </SimpleGrid>
        </Paper>

        {/* Cost Breakdown */}
        <Paper withBorder p="md">
          <Text fw={600} mb="xs">Cost Breakdown</Text>
          {breakdown.linked && breakdown.linked.length > 0 && (
            <div>
              <Text size="sm" fw={500} mb={4}>Linked Cost Records:</Text>
              {breakdown.linked.map((item, idx) => (
                <Group key={idx} justify="space-between" py={2}>
                  <Text size="sm">&bull; {item.description}</Text>
                  <Text size="sm" fw={500}>{fmt(item.amount)}</Text>
                </Group>
              ))}
            </div>
          )}
          {breakdown.freeform && breakdown.freeform.filter(i => i.description || i.amount).length > 0 && (
            <div style={{ marginTop: breakdown.linked?.length ? 12 : 0 }}>
              <Text size="sm" fw={500} mb={4}>Additional Line Items:</Text>
              {breakdown.freeform.filter(i => i.description || i.amount).map((item, idx) => (
                <Group key={idx} justify="space-between" py={2}>
                  <Text size="sm">&bull; {item.description} ({item.category || 'Other'})</Text>
                  <Text size="sm" fw={500}>{fmt(item.amount)}</Text>
                </Group>
              ))}
            </div>
          )}
          {(!breakdown.linked || breakdown.linked.length === 0) && (!breakdown.freeform || breakdown.freeform.filter(i => i.description || i.amount).length === 0) && (
            <Text size="sm" c="dimmed">No cost items recorded.</Text>
          )}

          <Divider my="sm" />

          <SimpleGrid cols={3} spacing="sm">
            <Paper p="sm" withBorder style={{ textAlign: 'center' }}>
              <Text size="xs" c="dimmed">Total</Text>
              <Text fw={700} size="lg">{fmt(reqAmt)}</Text>
            </Paper>
            <Paper p="sm" withBorder style={{ textAlign: 'center' }}>
              <Text size="xs" c="dimmed">Federal Share ({detailProj?.federalSharePct || '75'}%)</Text>
              <Text fw={700} size="lg" c="blue">{fmt(fedShare)}</Text>
            </Paper>
            <Paper p="sm" withBorder style={{ textAlign: 'center' }}>
              <Text size="xs" c="dimmed">Applicant Match</Text>
              <Text fw={700} size="lg">{fmt(appMatch)}</Text>
            </Paper>
          </SimpleGrid>
        </Paper>

        {/* Supporting Documents */}
        {docIds.length > 0 && (
          <Paper withBorder p="md">
            <Text fw={600} mb="xs">Supporting Documents</Text>
            {linkedDocs.length > 0
              ? linkedDocs.map(d => (
                  <Text key={d.Rid} size="sm" py={2}>
                    &bull; {d.documentName} ({d.documentType}) — ID: {d.Rid}
                  </Text>
                ))
              : docIds.map((id, idx) => (
                  <Text key={idx} size="sm" py={2}>&bull; Document ID: {id}</Text>
                ))
            }
          </Paper>
        )}

        {/* Reviewer Feedback Banner */}
        {selected.status === 'approved' && (
          <Alert color="green" title="Approved" icon={<IconCheck size={18} />}>
            Approved by {selected.reviewedBy} on {selected.reviewDate}.
            Amount: {fmt(parseFloat(selected.approvedAmount) || 0)}
            {selected.reviewNotes ? ` — ${selected.reviewNotes}` : ''}
          </Alert>
        )}
        {selected.status === 'denied' && (
          <Alert color="red" title="Denied" icon={<IconX size={18} />}>
            Denied by {selected.reviewedBy} on {selected.reviewDate}.
            {selected.reviewNotes ? ` — ${selected.reviewNotes}` : ''}
          </Alert>
        )}
        {selected.status === 'revision_needed' && (
          <Alert color="orange" title="Revision Requested" icon={<IconArrowBack size={18} />}>
            Revision requested by {selected.reviewedBy} on {selected.reviewDate}:
            {selected.reviewNotes ? ` ${selected.reviewNotes}` : ' No details provided.'}
          </Alert>
        )}
        {selected.status === 'paid' && (
          <Alert color="teal" title="Paid" icon={<IconCash size={18} />}>
            Paid on {selected.paymentDate}.
            {selected.paymentReference ? ` Ref: ${selected.paymentReference}` : ''}
          </Alert>
        )}

        {/* ── Action Buttons (role-dependent) ── */}
        {!isViewer && (
          <Paper withBorder p="md">
            {/* Applicant / creator actions */}
            {isApplicant && selected.status === 'draft' && (
              <Group>
                <Button leftSection={<IconEdit size={16} />} variant="light" onClick={() => openEdit(selected)}>Edit</Button>
                <Button leftSection={<IconSend size={16} />} loading={saving} onClick={() => handleSubmitFromDetail(selected)}>Submit</Button>
                <Button leftSection={<IconTrash size={16} />} color="red" variant="light" onClick={() => setDeleteTarget(selected)}>Delete</Button>
              </Group>
            )}
            {isApplicant && selected.status === 'revision_needed' && (
              <Group>
                <Button leftSection={<IconEdit size={16} />} variant="light" onClick={() => openEdit(selected)}>Edit</Button>
                <Button leftSection={<IconSend size={16} />} loading={saving} onClick={() => handleSubmitFromDetail(selected)}>Resubmit</Button>
                <Button leftSection={<IconTrash size={16} />} color="red" variant="light" onClick={() => setDeleteTarget(selected)}>Delete</Button>
              </Group>
            )}
            {isApplicant && selected.status === 'submitted' && (
              <Group>
                <Button leftSection={<IconArrowBack size={16} />} variant="light" loading={saving} onClick={() => handleRetract(selected)}>Retract to Draft</Button>
              </Group>
            )}

            {/* Grantee / admin actions */}
            {!isApplicant && (selected.status === 'submitted' || selected.status === 'under_review') && (
              <Stack gap="sm">
                <Text fw={600}>Review</Text>
                <NumberInput
                  label="Approved Amount"
                  prefix="$"
                  thousandSeparator=","
                  value={adjustedAmount === '' ? (parseFloat(selected.requestedAmount) || 0) : adjustedAmount}
                  onChange={setAdjustedAmount}
                />
                <Textarea
                  label="Review Notes"
                  placeholder="Comments for the applicant..."
                  value={reviewNotes}
                  onChange={e => setReviewNotes(e.currentTarget.value)}
                  rows={3}
                />
                <Group>
                  <Button color="green" leftSection={<IconCheck size={16} />} loading={saving} onClick={() => handleReviewAction(selected, 'approve')}>Approve</Button>
                  <Button color="orange" leftSection={<IconArrowBack size={16} />} loading={saving} onClick={() => handleReviewAction(selected, 'revision')}>Request Revision</Button>
                  <Button color="red" leftSection={<IconX size={16} />} loading={saving} onClick={() => handleReviewAction(selected, 'deny')}>Deny</Button>
                </Group>
              </Stack>
            )}
            {!isApplicant && selected.status === 'approved' && (
              <Stack gap="sm">
                <Text fw={600}>Mark as Paid</Text>
                <TextInput
                  label="Payment Reference (check #, wire ref)"
                  value={paymentRef}
                  onChange={e => setPaymentRef(e.currentTarget.value)}
                />
                <Group>
                  <Button leftSection={<IconCash size={16} />} color="teal" loading={saving} onClick={() => handleMarkPaid(selected)}>Mark as Paid</Button>
                </Group>
              </Stack>
            )}
            {!isApplicant && selected.status === 'draft' && (
              <Group>
                <Button leftSection={<IconEdit size={16} />} variant="light" onClick={() => openEdit(selected)}>Edit</Button>
                <Button leftSection={<IconSend size={16} />} loading={saving} onClick={() => handleSubmitFromDetail(selected)}>Submit</Button>
                <Button leftSection={<IconTrash size={16} />} color="red" variant="light" onClick={() => setDeleteTarget(selected)}>Delete</Button>
              </Group>
            )}
          </Paper>
        )}

        {/* Delete Confirm Modal */}
        <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} size="sm" title="Delete Reimbursement Request" closeOnClickOutside={false}>
          <Text>Are you sure you want to delete {deleteTarget?.requestNumber}?</Text>
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" onClick={handleDelete}>Delete</Button>
          </Group>
        </Modal>

        {/* Create/Edit Modal (reused) */}
        {renderCreateEditModal()}
      </Stack>
    );
  }

  // ══════════════════════════════════════════════════════════════
  //  LIST VIEW
  // ══════════════════════════════════════════════════════════════
  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3}>
          <IconReceipt size={24} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Reimbursement Requests
        </Title>
        <Group gap="xs">
          <Button leftSection={<IconDownload size={16} />} variant="light" size="xs" onClick={() => {
            exportToCSV('pa-reimbursements.csv',
              ['RFR #', 'Project', 'Applicant', 'Requested $', 'Approved $', 'Status', 'Submitted'],
              filtered.map(r => {
                const p = projMap.get(r.projectId);
                const o = orgMap.get(r.organizationId);
                return [r.requestNumber, p?.projectNumber || '', o?.orgName || '', r.requestedAmount, r.approvedAmount, r.status, r.submittedDate];
              })
            );
          }}>Export CSV</Button>
          {!isViewer && <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>New RFR</Button>}
        </Group>
      </Group>

      {/* Summary cards — grantees only */}
      {!isApplicant && (
        <SimpleGrid cols={4} spacing="sm">
          <Paper p="sm" withBorder>
            <Text size="xs" c="dimmed">Pending</Text>
            <Text fw={700} size="lg">{fmt(stats.pendingAmount)}</Text>
            <Text size="xs" c="dimmed">{stats.pendingCount} requests</Text>
          </Paper>
          <Paper p="sm" withBorder>
            <Text size="xs" c="dimmed">Approved This Month</Text>
            <Text fw={700} size="lg" c="green">{fmt(stats.approvedAmount)}</Text>
          </Paper>
          <Paper p="sm" withBorder>
            <Text size="xs" c="dimmed">Total Paid</Text>
            <Text fw={700} size="lg" c="teal">{fmt(stats.paidAmount)}</Text>
          </Paper>
          <Paper p="sm" withBorder>
            <Text size="xs" c="dimmed">Total RFRs</Text>
            <Text fw={700} size="lg">{sourceList.length}</Text>
          </Paper>
        </SimpleGrid>
      )}

      {/* Filters */}
      <Group gap="sm">
        <TextInput
          placeholder="Search..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.currentTarget.value)}
          style={{ flex: 1 }}
          size="sm"
        />
        <Select
          placeholder="All statuses"
          data={Object.entries(STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          value={statusFilter}
          onChange={setStatusFilter}
          clearable
          size="sm"
          style={{ width: 170 }}
        />
        {!isApplicant && (
          <Select
            placeholder="All applicants"
            data={organizations.map(o => ({ value: String(o.Rid), label: o.orgName }))}
            value={orgFilter}
            onChange={setOrgFilter}
            clearable
            searchable
            size="sm"
            style={{ width: 200 }}
          />
        )}
      </Group>

      {/* Table */}
      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>RFR #</Table.Th>
                <Table.Th>Project</Table.Th>
                {!isApplicant && <Table.Th>Applicant</Table.Th>}
                <Table.Th style={{ textAlign: 'right' }}>Requested</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Approved</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Submitted</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={isApplicant ? 6 : 7} style={{ textAlign: 'center', padding: 24 }}>
                    <Stack align="center" gap="xs">
                      <Text c="dimmed">No reimbursement requests found</Text>
                      {!isViewer && (
                        <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={openCreate}>
                          Start a New RFR
                        </Button>
                      )}
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              )}
              {filtered.map(rfr => {
                const p = projMap.get(rfr.projectId);
                const o = orgMap.get(rfr.organizationId);
                return (
                  <Table.Tr
                    key={rfr.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSelected(rfr);
                      setReviewNotes('');
                      setAdjustedAmount('');
                      setPaymentRef('');
                    }}
                  >
                    <Table.Td fw={500}>{rfr.requestNumber || '\u2014'}</Table.Td>
                    <Table.Td>
                      {p?.projectNumber || '\u2014'}
                      <br />
                      <Text size="xs" c="dimmed">{p?.projectTitle || ''}</Text>
                    </Table.Td>
                    {!isApplicant && <Table.Td>{o?.orgName || '\u2014'}</Table.Td>}
                    <Table.Td style={{ textAlign: 'right' }}>
                      {rfr.requestedAmount ? fmt(parseFloat(rfr.requestedAmount)) : '\u2014'}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {rfr.approvedAmount ? fmt(parseFloat(rfr.approvedAmount)) : '\u2014'}
                    </Table.Td>
                    <Table.Td>
                      <Badge color={STATUS_COLORS[rfr.status] || 'gray'} size="sm">
                        {STATUS_LABELS[rfr.status] || rfr.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{rfr.submittedDate || rfr.requestDate || '\u2014'}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </div>
      </Paper>

      {/* Create/Edit Modal */}
      {renderCreateEditModal()}

      {/* Delete Confirm */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} size="sm" title="Delete Reimbursement Request" closeOnClickOutside={false}>
        <Text>Are you sure you want to delete {deleteTarget?.requestNumber}?</Text>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="red" onClick={handleDelete}>Delete</Button>
        </Group>
      </Modal>
    </Stack>
  );

  // ══════════════════════════════════════════════════════════════
  //  CREATE / EDIT MODAL (shared between list and detail views)
  // ══════════════════════════════════════════════════════════════
  function renderCreateEditModal() {
    return (
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        size="xl"
        title={editing ? `Edit ${editing.requestNumber}` : 'New Reimbursement Request'}
        closeOnClickOutside={false}
      >
        {/* Revision feedback banner inside modal */}
        {editing?.status === 'revision_needed' && editing.reviewNotes && (
          <Alert color="orange" title="Revision Requested" icon={<IconArrowBack size={18} />} mb="md">
            {editing.reviewedBy} on {editing.reviewDate}: {editing.reviewNotes}
          </Alert>
        )}

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="info">Request Info</Tabs.Tab>
            <Tabs.Tab value="costs">Cost Breakdown</Tabs.Tab>
            <Tabs.Tab value="docs">Supporting Docs</Tabs.Tab>
            <Tabs.Tab value="summary">Summary</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="info" pt="md">
            <Stack gap="sm">
              <Select
                label="Project"
                placeholder="Select a project"
                data={availableProjects.map(p => ({ value: String(p.Rid), label: `${p.projectNumber} \u2014 ${p.projectTitle}` }))}
                value={form.projectId}
                onChange={v => v && handleProjectChange(v)}
                searchable
              />
              <SimpleGrid cols={3} spacing="sm">
                <TextInput label="RFR Number" value={form.requestNumber} readOnly />
                <TextInput label="Period Start" type="date" value={form.periodStart} onChange={e => setForm(f => ({ ...f, periodStart: e.currentTarget.value }))} />
                <TextInput label="Period End" type="date" value={form.periodEnd} onChange={e => setForm(f => ({ ...f, periodEnd: e.currentTarget.value }))} />
              </SimpleGrid>
              <Textarea label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.currentTarget.value }))} rows={2} />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="costs" pt="md">
            <Stack gap="md">
              {/* Linked existing costs */}
              <div>
                <Group gap="xs" mb="xs">
                  <ThemeIcon size="sm" variant="light" color="blue"><IconLink size={12} /></ThemeIcon>
                  <Text fw={600} size="sm">Link Existing Cost Records</Text>
                </Group>
                {availableCosts.length === 0 ? (
                  <Text size="sm" c="dimmed">Select a project first, or no cost records exist for this project.</Text>
                ) : (
                  <Paper withBorder p="xs" style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {availableCosts.map(item => {
                      const isLinked = form.linkedItems.some(i => i.type === item.type && i.rid === item.rid);
                      return (
                        <Group key={`${item.type}-${item.rid}`} justify="space-between" py={4} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', cursor: 'pointer' }} onClick={() => toggleLinkedItem(item)}>
                          <Group gap="xs">
                            <Badge size="xs" variant={isLinked ? 'filled' : 'outline'} color={isLinked ? 'blue' : 'gray'}>{isLinked ? '\u2713' : '\u25CB'}</Badge>
                            <Text size="sm">{item.description}</Text>
                          </Group>
                          <Text size="sm" fw={500}>{fmt(item.amount)}</Text>
                        </Group>
                      );
                    })}
                  </Paper>
                )}
                {form.linkedItems.length > 0 && (
                  <Text size="sm" mt="xs" fw={500}>Linked subtotal: {fmt(form.linkedItems.reduce((s, i) => s + i.amount, 0))}</Text>
                )}
              </div>

              <Divider />

              {/* Freeform items */}
              <div>
                <Group gap="xs" mb="xs">
                  <ThemeIcon size="sm" variant="light" color="violet"><IconRowInsertBottom size={12} /></ThemeIcon>
                  <Text fw={600} size="sm">Additional Line Items</Text>
                </Group>
                {form.freeformItems.map((item, idx) => (
                  <Group key={idx} gap="xs" mb="xs" align="flex-end">
                    <TextInput label={idx === 0 ? 'Description' : undefined} placeholder="Description" value={item.description} onChange={e => {
                      const items = [...form.freeformItems];
                      items[idx] = { ...items[idx], description: e.currentTarget.value };
                      setForm(f => ({ ...f, freeformItems: items }));
                    }} style={{ flex: 2 }} size="sm" />
                    <Select label={idx === 0 ? 'Category' : undefined} placeholder="Category" data={['Labor', 'Equipment', 'Materials', 'Contracts', 'Rented Equipment', 'Other']} value={item.category} onChange={v => {
                      const items = [...form.freeformItems];
                      items[idx] = { ...items[idx], category: v || '' };
                      setForm(f => ({ ...f, freeformItems: items }));
                    }} style={{ flex: 1 }} size="sm" clearable />
                    <NumberInput label={idx === 0 ? 'Amount' : undefined} placeholder="0.00" prefix="$" thousandSeparator="," value={item.amount || ''} onChange={v => {
                      const items = [...form.freeformItems];
                      items[idx] = { ...items[idx], amount: typeof v === 'number' ? v : 0 };
                      setForm(f => ({ ...f, freeformItems: items }));
                    }} style={{ flex: 1 }} size="sm" />
                    <ActionIcon size="sm" variant="subtle" color="red" onClick={() => {
                      const items = form.freeformItems.filter((_, i) => i !== idx);
                      setForm(f => ({ ...f, freeformItems: items.length ? items : [{ description: '', category: '', amount: 0 }] }));
                    }}><IconTrash size={14} /></ActionIcon>
                  </Group>
                ))}
                <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} onClick={() => setForm(f => ({ ...f, freeformItems: [...f.freeformItems, { description: '', category: '', amount: 0 }] }))}>
                  Add Line Item
                </Button>
              </div>

              <Divider />
              <SimpleGrid cols={3}>
                <Paper p="xs" withBorder><Text size="xs" c="dimmed">Total Requested</Text><Text fw={700}>{fmt(formTotals.total)}</Text></Paper>
                <Paper p="xs" withBorder><Text size="xs" c="dimmed">Federal Share ({formProj?.federalSharePct || '75'}%)</Text><Text fw={700} c="blue">{fmt(formTotals.federalShare)}</Text></Paper>
                <Paper p="xs" withBorder><Text size="xs" c="dimmed">Applicant Match</Text><Text fw={700}>{fmt(formTotals.applicantMatch)}</Text></Paper>
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="docs" pt="md">
            <Stack gap="sm">
              <Text size="sm" c="dimmed">Select supporting documents from this project's document library.</Text>
              <MultiSelect
                label="Supporting Documents"
                placeholder="Select documents..."
                searchable
                data={documents.filter(d => d.projectId === form.projectId).map(d => ({
                  value: String(d.Rid),
                  label: `${d.documentName || 'Untitled'} (${d.documentType || 'Unknown'})`,
                }))}
                value={form.supportingDocIds ? form.supportingDocIds.split(',').map(s => s.trim()).filter(Boolean) : []}
                onChange={vals => setForm(f => ({ ...f, supportingDocIds: vals.join(', ') }))}
                nothingFoundMessage={form.projectId ? 'No documents for this project' : 'Select a project first'}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="summary" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={2} spacing="sm">
                <div><Text size="xs" c="dimmed">RFR Number</Text><Text fw={500}>{form.requestNumber || '\u2014'}</Text></div>
                <div><Text size="xs" c="dimmed">Project</Text><Text fw={500}>{projMap.get(form.projectId)?.projectNumber || '\u2014'} \u2014 {projMap.get(form.projectId)?.projectTitle || ''}</Text></div>
                <div><Text size="xs" c="dimmed">Applicant</Text><Text fw={500}>{orgMap.get(form.organizationId)?.orgName || '\u2014'}</Text></div>
                <div><Text size="xs" c="dimmed">Period</Text><Text fw={500}>{form.periodStart || '\u2014'} to {form.periodEnd || '\u2014'}</Text></div>
              </SimpleGrid>
              <Divider />
              <SimpleGrid cols={3}>
                <Paper p="sm" withBorder style={{ textAlign: 'center' }}><Text size="xs" c="dimmed">Total</Text><Text fw={700} size="lg">{fmt(formTotals.total)}</Text></Paper>
                <Paper p="sm" withBorder style={{ textAlign: 'center' }}><Text size="xs" c="dimmed">Federal Share</Text><Text fw={700} size="lg" c="blue">{fmt(formTotals.federalShare)}</Text></Paper>
                <Paper p="sm" withBorder style={{ textAlign: 'center' }}><Text size="xs" c="dimmed">Applicant Match</Text><Text fw={700} size="lg">{fmt(formTotals.applicantMatch)}</Text></Paper>
              </SimpleGrid>
              {form.linkedItems.length > 0 && (
                <div>
                  <Text size="sm" fw={500}>Linked Cost Records ({form.linkedItems.length})</Text>
                  {form.linkedItems.map((i, idx) => <Text key={idx} size="sm">&bull; {i.description} \u2014 {fmt(i.amount)}</Text>)}
                </div>
              )}
              {form.freeformItems.filter(i => i.description || i.amount).length > 0 && (
                <div>
                  <Text size="sm" fw={500}>Additional Line Items</Text>
                  {form.freeformItems.filter(i => i.description || i.amount).map((i, idx) => <Text key={idx} size="sm">&bull; {i.description} ({i.category}) \u2014 {fmt(i.amount)}</Text>)}
                </div>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button variant="light" loading={saving} onClick={() => handleSave(false)} disabled={!form.projectId}>Save Draft</Button>
          <Button loading={saving} onClick={() => handleSave(true)} disabled={!form.projectId}>Save &amp; Submit</Button>
        </Group>
      </Modal>
    );
  }
}
